'use client';

import { useRightDrawer } from '@/contexts/DrawerContext';
import { useMarketNews } from '@/hooks/use-market-news';
import { useTranslation } from '@/lib/i18n';
import { formatTimeAgo } from '@/lib/utils/date';
import { Avatar, Skeleton } from '@mui/material';
import { range } from 'lodash';
import { Star } from 'lucide-react';
import { useEffect } from 'react';
import ImpactMarket, { MarketEvent } from './ImpactMarket';
import Image from 'next/image';
import FixedChatWindow from '../message/FixedChatWindow';
import PythraSummary from '../ui/PythraSummary';

interface NewsDetailProps {
  id?: string;
  openInSidebar?: boolean;
}
export default function NewsDetail({ id, openInSidebar }: NewsDetailProps) {
  const { t } = useTranslation();

  const { news, events, isLoading } = useMarketNews({
    newsId: id,
    data: openInSidebar ? 'news' : undefined,
  });

  const poster =
    events.find((event) => event?.image)?.image ||
    events.find((event) => event?.icon)?.icon ||
    null;

  if (isLoading) {
    return <LoadingHolder />;
  }

  if (news) {
    const importanceLevel = news.importance_level || 0;

    return (
      <>
        <div className="max-w-245 mx-auto pb-25">
          <h1 className="font-bold text-[40px] leading-11 mb-2.5">{news.title}</h1>
          <div className="flex items-center gap-3 text-[#757575] text-sm">
            <Avatar
              sx={{ width: '16px', height: '16px', bgcolor: 'rgba(255,255,255,.5)' }}
              alt={news.source}
              src={news.source_icon_url || undefined}
            />
            <span>{news.source}</span>
            <span>{formatTimeAgo(news.publish_time)}</span>
            <div className="flex items-center gap-1">
              {t('Event impact')}:
              {range(importanceLevel).map((item) => (
                <Star size={16} fill="#FFBE49" stroke="#FFBE49" key={item} />
              ))}
              {range(Math.max(5 - importanceLevel, 0)).map((item) => (
                <Star size={16} fill="#3A3C44" stroke="#3A3C44" key={item} />
              ))}
            </div>
          </div>

          {!openInSidebar && (
            <PythraSummary summary={news.summary} className="mt-5" loading={isLoading} />
          )}

          {poster && !openInSidebar && (
            <Image
              src={poster}
              width={560}
              height={260}
              alt=""
              className="mx-auto mt-8 w-140 h-auto rounded-[14px]"
            />
          )}
          <article className="leading-7 mt-8">{news.content}</article>
          <div className="py-10 flex items-center justify-center text-[#757575] text-[15px]">
            <span>———&nbsp;{t('End')}&nbsp;———</span>
          </div>
        </div>

        {!openInSidebar && <ImpactMarketsRender events={events} />}

        {!isLoading && !openInSidebar && (
          <FixedChatWindow
            suggestions={news?.recommended_questions || []}
            sourceId={`news_${news?.news_id}`}
            userContext={
              news
                ? JSON.stringify({
                    context_type: 'news',
                    data: {
                      ...news,
                      trending_card: news,
                    },
                  })
                : undefined
            }
          />
        )}
      </>
    );
  }
  return <div>{t('No Data')}</div>;
}

function LoadingHolder() {
  return (
    <div className="pt-10 mx-auto">
      <Skeleton variant="text" width="80%" sx={{ fontSize: '1.5rem', bgcolor: 'grey.800' }} />
      <div className="flex items-center gap-3 text-[#757575] mt-3">
        <Skeleton variant="rounded" width={16} height={16} sx={{ bgcolor: 'grey.800' }} />
        <Skeleton variant="text" width={120} sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }} />
        <div className="flex items-center gap-1">
          <Skeleton variant="text" width={120} sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }} />
        </div>
      </div>
      <article className="leading-7 mt-8">
        <Skeleton variant="text" sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }} />
        <Skeleton variant="text" sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }} />
        <Skeleton variant="text" sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }} />
      </article>
    </div>
  );
}

interface ImpactMarketsRenderProps {
  events: MarketEvent[];
}
function ImpactMarketsRender({ events }: ImpactMarketsRenderProps) {
  const { t } = useTranslation();
  const { openDrawer } = useRightDrawer();

  useEffect(() => {
    if (events.length && events.every((event) => !!event.markets?.length)) {
      openDrawer(<ImpactMarket markets={events} />, t('Impacted Markets'), true);
    }
  }, [events, openDrawer, t]);

  return null;
}
